import {useEffect} from 'react';
import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import * as Sentry from '@sentry/browser';
import environment from '../../../../RelayEnvironment';
import type {useNovaAvailableAgentsQuery} from './__generated__/useNovaAvailableAgentsQuery.graphql';
import {setAvailableAgents} from './novaInvocationStore';

const query = graphql`
	query useNovaAvailableAgentsQuery {
		viewer {
			novaAgentsForInvocation {
				agentId
				title
				description
				category
				isDefault
			}
		}
	}
`;

// The agent list is one shared, per-session thing — not per row, not per task — so this fetches
// once regardless of how many mounted controls call the hook, and every one of them shares the
// result through novaInvocationStore rather than each holding its own copy.
//
// Held as a module-level Promise, with the store written to from inside that promise's own .then
// — not from a hook's local state, which useForecastFetchQuery would give instead.
// ConnectedNovaInvocationControl mounts once per task row, and a row can unmount mid-flight well
// before the fetch resolves (scrolling a virtualized list, a filter change, a route change). With
// the write living in per-component React state, that row's data and its own effect are simply
// never applied once it has unmounted — the real list never reaches the store — and a plain
// boolean "already tried" flag, reset only on the failure path, then stays true forever: no later
// mount ever retries either. A promise living on the module rather than the component has nothing
// to unmount out from under it.
let fetchPromise: Promise<void> | null = null;

// Exposed for tests only, so each test starts from a clean slate rather than inheriting whichever
// spec ran first — no production caller has a reason to reset a real fetch that already happened.
export function resetNovaAvailableAgentsFetchForTests(): void {
	fetchPromise = null;
}

function startFetch(): Promise<void> {
	return fetchQuery(environment.getInstance(), query, {})
		.toPromise()
		.then(
			response => {
				const data = response as useNovaAvailableAgentsQuery['response'] | null | undefined;
				if (data?.viewer?.novaAgentsForInvocation) {
					setAvailableAgents(
						data.viewer.novaAgentsForInvocation.map(agent => ({
							id: agent.agentId,
							title: agent.title,
							description: agent.description ?? undefined,
							category: agent.category,
							isDefault: agent.isDefault,
						}))
					);
				}
			},
			() => {
				// The rejection here carries no error object worth reporting individually — this can
				// only report that the fetch failed, not why. A message capture rather than
				// captureException for the same reason. Without this, a failed fetch was previously
				// silent: the store keeps whatever it had, and nothing anywhere — not the console, not
				// monitoring — indicates the list shown is not real.
				Sentry.captureMessage(
					'novaAgentsForInvocation fetch failed; falling back to the last known agent list',
					'warning'
				);
				// Cleared rather than left set, so the next mount that calls this hook (e.g.
				// navigating away and back) tries again instead of giving up on real data for one
				// transient failure, for the rest of the tab.
				fetchPromise = null;
			}
		);
}

export function useNovaAvailableAgents(enabled: boolean): void {
	useEffect(() => {
		if (!enabled || fetchPromise) return;
		fetchPromise = startFetch();
	}, [enabled]);
}
